import {nbsp} from 'legacy/utils';
import translation from 'translation';
import store from 'store';
import sellerStore from 'store/indexSeller';
import {isCurrentAppSeller} from 'utils/appTypeState';
import {relativeTimeWithPlural} from 'translation/moment/ru';

const isDevelopment = () => isCurrentAppSeller()
    ? sellerStore.getters['sellerCommon/isDevelopment']
    : store.getters['common/isDevelopment'];

const currencySymbol = () => isCurrentAppSeller()
    ? sellerStore.getters['sellerCommon/activeCurrencySymbol']
    : store.getters['common/activeCurrencySymbol'];

const defaults = () => {
    const d = translation.numeralDelimeters.get();
    return {
        digitGroupSeparator: d.thousands,
        decimalCharacter: d.decimal,
        decimalCharacterAlternative: '.',
        roundingMethod: 'B',
        showWarnings: isDevelopment(),
        selectOnFocus: false,
        emptyInputBehavior: 'null',
        modifyValueOnWheel: false,
        overrideMinMaxLimits: 'ceiling',
        onInvalidPaste: 'ignore',
        minimumValue: '0',
        maximumValue: '99999999999999',
        decimalPlaces: 2
    };
};

const withNegative = (options) => {
    const copy = {...options};
    if (copy.negative && copy.maximumValue) {
        copy.minimumValue = `-${copy.maximumValue}`;
    }
    return copy;
};
const withSymbol = (options, symbol) => {
    const copy = {...options};
    if (copy.symbol) {
        copy.currencySymbol = symbol;
        copy.currencySymbolPlacement = 's';
    }
    return copy;
};
const config = ({filter = null, optionDefaults = {}, parent = defaults}) => (options = {}, value = null) => {
    const parentObj = parent ? parent() : {};
    options = {...parentObj, ...optionDefaults, ...options};
    return {
        ...parentObj,
        ...filter ? filter(options, value) : {}
    };
};

const id = config({
    filter: options => ({
        ..._.pick(options, [
            'minimumValue',
            'maximumValue'
        ]),
        decimalPlaces: 0,
        digitGroupSeparator: '',
        nullable: true
    })
});

const integer = config({
    filter (options) {
        const optionsWithNegative = withNegative(options);
        return {
            ..._.pick(optionsWithNegative, [
                'minimumValue',
                'maximumValue'
            ]),
            decimalPlaces: 0
        };
    },
    optionDefaults: {
        negative: false
    }
});

const dayHour = config({
    filter: () => ({
        minimumValue: '0',
        maximumValue: '23'
    }),
    parent: integer
});

const decimal = config({
    filter (options) {
        const optionsWithNegative = withNegative(options);
        return {
            ..._.pick(optionsWithNegative, [
                'minimumValue',
                'maximumValue',
                'allowDecimalPadding'
            ]),
            decimalPlaces: 8
        };
    },
    optionDefaults: {
        negative: false,
        allowDecimalPadding: false
    }
});

const decimalShort = (args) => ({...decimal(args), decimalPlaces: 2});

const percent = config({
    filter (options) {
        const optionsWithSymbol = withSymbol(options, '%');
        return {
            decimalPlaces: 2,
            ..._.pick(optionsWithSymbol, [
                'minimumValue',
                'maximumValue',
                'currencySymbol',
                'currencySymbolPlacement',
                'allowDecimalPadding',
                'decimalPlaces'
            ])
        };
    },
    optionDefaults: {
        symbol: true,
        decimalPlaces: 2,
        maximumValue: '100'
    },
    parent: decimal
});

const minutes = config({
    filter (options, value) {
        const pluralText = relativeTimeWithPlural(value, null, 'mm', true).toLowerCase();
        const optionsWithSymbol = withSymbol(options, nbsp + pluralText);
        return {
            decimalPlaces: 2,
            ..._.pick(optionsWithSymbol, [
                'minimumValue',
                'maximumValue',
                'currencySymbol',
                'currencySymbolPlacement',
                'allowDecimalPadding'
            ])
        };
    },
    optionDefaults: {
        symbol: true
    },
    parent: decimal
});

const coefficient = config({
    minimumValue: '0',
    filter: options => _.pick(options, [
        'minimumValue',
        'maximumValue',
        'allowDecimalPadding'
    ]),
    optionDefaults: {
        maximumValue: '9999'
    },
    parent: decimal
});

const currency = config({
    filter (options) {
        const optionsWithNegative = withNegative(options);
        const symbol = currencySymbol();
        const optionsWithNegativeAndSymbol = symbol
            ? withSymbol(optionsWithNegative, nbsp + symbol) : optionsWithNegative;

        return {
            ..._.pick(optionsWithNegativeAndSymbol, [
                'minimumValue',
                'maximumValue',
                'currencySymbol',
                'currencySymbolPlacement',
                'allowDecimalPadding',
                'decimalPlaces'
            ])
        };
    },
    optionDefaults: {
        negative: false,
        symbol: true,
        vat: true,
        vatInversed: false,
        allowDecimalPadding: true,
        decimalPlaces: 2
    }
});

const currencyRate = config({
    filter (options) {
        const symbol = currencySymbol();
        const optionsWithSymbol = withSymbol(options, nbsp + symbol);
        return {
            ..._.pick(optionsWithSymbol, [
                'minimumValue',
                'maximumValue',
                'currencySymbol',
                'currencySymbolPlacement',
                'allowDecimalPadding'
            ]),
            decimalPlaces: 8
        };
    },
    optionDefaults: {
        symbol: true,
        allowDecimalPadding: false
    }
});


export {defaults, id, integer, minutes, dayHour, percent, decimal, decimalShort, coefficient, currency, currencyRate};
